/**
 * VGreen configurator — interactive behavior.
 *
 * Three independent concerns:
 *   1. Customize toggle (Tier 1 → Tier 2 disable/enable).
 *   2. Buckle width derivation: explicit choice from the customer OR tapered
 *      formula `(lug - lug%2) - 2` when "I'll let you decide" is selected.
 *      Result is mirrored into a hidden `properties[Buckle width]` input.
 *   3. Buckle inventory filter: shows only buckles whose `available_widths`
 *      contains the effective buckle width. Combined with a "VGreen provides
 *      / Use my own" source toggle. Inventory radios share
 *      `name="properties[Buckle]"` with the hidden own-buckle radio so the
 *      group stays mutually exclusive.
 *
 * Disabled inputs are excluded from FormData on submit. If JS fails, the
 * default static values still submit — acceptable degradation.
 */
(function () {
  // ============================================================
  // 0. Bundle-grouping helper (used by handleSubmit when companions
  // are present). Generates a unique ID shared by every line item in
  // a bundled add-to-cart so assets/vgreen-cart-grouping.js can
  // cascade-remove them together. Prefers crypto.randomUUID where
  // available; falls back to timestamp + Math.random.
  // ============================================================
  function generateBundleGroupId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return 'vg-' + window.crypto.randomUUID();
    }
    return 'vg-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  // ============================================================
  // 1. Customize toggle
  // ============================================================
  function initCustomizeToggle(configurator) {
    const panel = configurator.querySelector('.vgreen-conditional-panel--customize');
    if (!panel) return;

    const tier2Inputs = panel.querySelectorAll('input, select, textarea');
    const customizeYes = configurator.querySelector(
      'input[data-vgreen-conditional="customize-yes"]'
    );
    if (!customizeYes) return;

    // Capture initial DOM defaults so we can restore them when the customer
    // toggles Customize back to "No, use defaults" — without this the
    // customer's previous Tier 2 picks would persist if they re-enable
    // customize, which is unexpected and confusing.
    const initialState = new Map();
    tier2Inputs.forEach((input) => {
      if (input.type === 'radio' || input.type === 'checkbox') {
        initialState.set(input, { checked: input.defaultChecked });
      } else if (input.tagName === 'SELECT') {
        const def = Array.from(input.options).find((o) => o.defaultSelected);
        initialState.set(input, {
          value: def ? def.value : (input.options[0] ? input.options[0].value : '')
        });
      } else {
        initialState.set(input, { value: input.defaultValue || '' });
      }
    });

    function resetTier2Values() {
      tier2Inputs.forEach((input) => {
        const init = initialState.get(input);
        if (!init) return;
        if (input.type === 'radio' || input.type === 'checkbox') {
          input.checked = init.checked;
        } else {
          input.value = init.value;
        }
      });
    }

    function syncTier2State() {
      const enable = customizeYes.checked;
      // Reset values BEFORE disabling so the next "Yes" toggle starts
      // from a clean slate. Disabled inputs are excluded from FormData,
      // so the companion summary will recompute correctly when this
      // change event bubbles up to the configurator listener.
      if (!enable) {
        resetTier2Values();
      }
      tier2Inputs.forEach((input) => {
        input.disabled = !enable;
      });
    }

    syncTier2State();

    const customizeRadios = configurator.querySelectorAll(
      'input[name="' + customizeYes.name + '"]'
    );
    customizeRadios.forEach((radio) => {
      radio.addEventListener('change', syncTier2State);
    });
  }

  // ============================================================
  // 1a. Keep marketing params in the URL across variant changes
  // ============================================================
  // Dawn's product-info.js updateURL() rebuilds the address as
  // `${url}?variant=${id}`, discarding EVERY other query param and the hash on
  // every variant change — utm_*, gclid, in-page anchors. This re-merges them
  // after Dawn has written. Implemented here rather than by editing Dawn's
  // core file (ported from CA, where that decision was made after the dev
  // copy of product-info.js was found never to have been diffed against live).
  function initPreserveUrlParams() {
    if (window.__vgUrlParamsGuard) return; // once per page, not per configurator
    window.__vgUrlParamsGuard = true;
    let sticky = [];
    let stickyHash = '';
    try {
      // `window.__vgOriginalUrl` is set by an inline script at parse time in
      // the cartier snippet, so it survives the case where something has
      // already triggered Dawn's rewrite before this deferred file runs.
      const initial = new URL(window.__vgOriginalUrl || window.location.href);
      initial.searchParams.forEach((value, key) => {
        // `variant` is owned by Dawn. `leather` is a one-shot landing
        // instruction and must NOT be preserved: keeping it made the URL
        // contradict the chosen variant after a leather switch (CA finding).
        if (key === 'variant' || key === 'leather') return;
        sticky.push([key, value]);
      });
      stickyHash = initial.hash || '';
    } catch (e) {
      return;
    }
    if (!sticky.length && !stickyHash) return;

    function repair() {
      try {
        const cur = new URL(window.location.href);
        let changed = false;
        sticky.forEach(([key, value]) => {
          if (!cur.searchParams.has(key)) {
            cur.searchParams.append(key, value);
            changed = true;
          }
        });
        if (stickyHash && !cur.hash) {
          cur.hash = stickyHash;
          changed = true;
        }
        if (changed) {
          window.history.replaceState(
            window.history.state,
            '',
            cur.pathname + cur.search + cur.hash
          );
        }
        return changed;
      } catch (e) {
        /* never let URL cosmetics break the page */
      }
    }

    // Re-merge immediately after ANY history rewrite, whoever performs it.
    // `inRepair` stops the recursion, since repair() itself calls replaceState.
    let inRepair = false;
    ['replaceState', 'pushState'].forEach((method) => {
      const original = window.history[method];
      if (typeof original !== 'function') return;
      window.history[method] = function () {
        const result = original.apply(window.history, arguments);
        if (!inRepair) {
          inRepair = true;
          try { repair(); } finally { inRepair = false; }
        }
        return result;
      };
    });

    document.addEventListener(
      'change',
      (event) => {
        const t = event.target;
        if (!t || !t.closest) return;
        if (t.closest('variant-selects') || t.closest('variant-radios')) {
          setTimeout(repair, 0);
          setTimeout(repair, 500);
        }
      },
      true
    );
    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.variantChange
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(repair, 0));
    }
  }

  // ============================================================
  // 1b. Stitch colour vs "no stitching"
  // ============================================================
  // Picking "no stitching" left the stitch-colour group enabled, so the order
  // still carried a colour for stitching that does not exist. Hide and
  // DISABLE the group in that state (disabled inputs are excluded from
  // FormData). Detected by VALUE so it works on every configurator snippet;
  // the colour group is located via the existing `data-stitch-matching`
  // attribute. JP: the metaobject labels are Japanese, so the pattern covers
  // ステッチなし / ステッチ無し / ノーステッチ as well as the English form —
  // VERIFY the actual label on a JP product before relying on it.
  //
  // MUST be initialised AFTER initCustomizeToggle: that blanket-sets
  // `disabled = !customizeYes.checked` on every Tier 2 input, so our listener
  // has to run after its listener on the same radios to have the last word.
  const NO_STITCH_RE = /no\s*stitch|ステッチ\s*(なし|無し)|ノー\s*ステッチ/i;
  function initStitchColorGating(configurator) {
    const allRadios = Array.from(
      configurator.querySelectorAll('input[type="radio"]')
    );
    const noStitch = allRadios.find((r) => NO_STITCH_RE.test(r.value || ''));
    if (!noStitch) return; // product has no "no stitching" option
    const styleRadios = allRadios.filter((r) => r.name === noStitch.name);

    const matching = configurator.querySelector('input[data-stitch-matching]');
    const colorField = matching ? matching.closest('fieldset') : null;
    if (!colorField) return;
    const colorInputs = Array.from(colorField.querySelectorAll('input'));
    const note = configurator.querySelector('.vgreen-stitch-matching-note');
    const customizeYes = configurator.querySelector(
      'input[data-vgreen-conditional="customize-yes"]'
    );

    function sync() {
      const isNone = styleRadios.some(
        (r) => r.checked && NO_STITCH_RE.test(r.value || '')
      );
      const customizing = !customizeYes || customizeYes.checked;
      colorField.style.display = isNone ? 'none' : '';
      if (note) note.style.display = isNone ? 'none' : '';
      // Never enable while Tier 2 is collapsed — that is initCustomizeToggle's
      // rule and this must not override it.
      colorInputs.forEach((input) => {
        input.disabled = isNone || !customizing;
      });
    }

    styleRadios.forEach((r) => r.addEventListener('change', sync));
    if (customizeYes) {
      configurator
        .querySelectorAll('input[name="' + customizeYes.name + '"]')
        .forEach((r) => r.addEventListener('change', sync));
    }
    sync();
  }

  // ============================================================
  // 2 + 3. Buckle width + source + inventory filter
  // ============================================================
  function initBuckle(configurator) {
    const widthChoiceSelect = configurator.querySelector('.vgreen-buckle-width-choice');
    const widthSubmitInput = configurator.querySelector('.vgreen-buckle-width-submit');
    const provideRadio = configurator.querySelector(
      'input[data-vgreen-conditional="buckle-provide"]'
    );
    const ownRadio = configurator.querySelector(
      'input[data-vgreen-conditional="buckle-own"]'
    );
    const ownBuckleRadio = configurator.querySelector('.vgreen-buckle-own-radio');
    const emptyNote = configurator.querySelector('.vgreen-buckle-empty-note');
    // Required inline buckle-photo upload, present on every configurator
    // that exposes the "use my own buckle" branch (custom-strap, simple-strap,
    // apple-watch — as of 2026-05-27). Disabled by default in the snippet;
    // enabled by syncBuckleSource when the customer toggles to "I'll use my
    // own buckle" — disabled inputs are excluded from FormData so we don't
    // ship an empty Buckle photo LIP on every provide-branch order. Submit-
    // time enforcement lives in initRequireBucklePhoto below.
    const ownPhotoInput = configurator.querySelector('.vgreen-buckle-photo-input');
    const ownPhotoError = configurator.querySelector('.vgreen-file-error');

    function getInventoryRadios() {
      return Array.from(
        configurator.querySelectorAll('input[data-buckle-option]')
      );
    }

    // Which buckle to restore when nothing is checked any more.
    // Ported from CA (final form after review, 2026-08-25). Order:
    //   1. what the customer last EXPLICITLY chose, if still selectable
    //   2. the authored default — the radio carrying the `checked` ATTRIBUTE
    //      in the server-rendered HTML (the attribute survives the width
    //      filter clearing the live `.checked` property)
    //   3. first visible (historical behaviour)
    // Why: when the landing variant is a size PLACEHOLDER no lug width
    // resolves, so syncInventory hides and unchecks every buckle; the old
    // "first visible wins" restore then selected entry #1 on the next size
    // pick, and the own-buckle radio (which shares properties[バックル]) did
    // the same on own -> provide. Both restore branches use this helper.
    // Derived from the CURRENT radios on every call, so it cannot go stale.
    let lastExplicitBuckle = null;
    configurator.addEventListener('change', (event) => {
      const t = event.target;
      // Our own restores use `.checked = true`, which dispatches no `change`,
      // so they cannot be recorded here as if they were deliberate choices.
      // (A third-party script calling .click() or dispatching a synthetic
      // change WOULD be recorded — treated as indistinguishable from a real
      // choice on purpose.)
      if (t && t.matches && t.matches('input[data-buckle-option]') && t.checked) {
        lastExplicitBuckle = t.value;
      }
    });

    function preferredDefaultRadio(visible) {
      if (!visible || !visible.length) return null;
      if (lastExplicitBuckle) {
        const remembered = visible.find((r) => r.value === lastExplicitBuckle);
        if (remembered) return remembered;
      }
      return visible.find((r) => r.hasAttribute('checked')) || visible[0];
    }

    // -------- Lug detection (pubsub-driven, with fallbacks) --------
    // Primary path: subscribe to Dawn's PUB_SUB_EVENTS.variantChange (defined
    // in assets/constants.js, published from assets/product-info.js). The
    // event delivers `event.data.variant` with the full variant object whose
    // `.options` array we read at the position recorded in Liquid (via the
    // `data-lug-option-position` attribute on the configurator div). Stable
    // across Dawn theme bumps because pubsub is documented public API,
    // unlike DOM class names / element wrappers.
    //
    // Fallback A (page-load init): variant-selects ships with a
    //   <script type="application/json" data-selected-variant>{...}</script>
    //   block whose contents are the currently-selected variant. Read it on
    //   init so we have a lug value before the customer interacts.
    //
    // Fallback B (last resort): the original DOM-walking implementation,
    //   used only if pubsub isn't available AND data-selected-variant
    //   parsing failed. Quietly handles any future-proofing edge case.
    const lugOptionPosition = parseInt(
      configurator.dataset.lugOptionPosition || '-1',
      10
    );
    let cachedLugWidth = null;

    // -------- Optional per-configurator value→width map --------
    // For integrated-bracelet products (e.g. Tissot PRX) the option at
    // `lugOptionPosition` is not a real lug width — it's a watch model
    // name ("35mm", "40mm"). The first-digit regex would parse 35 or 40,
    // both outside the buckle manufacturing range. Snippet can pass
    // `data-option-value-buckle-width-map='{"35mm":16,"40mm":18}'` to
    // map each option value directly to a buckle width. When a value
    // resolves via the map, taperedDefault treats it as the buckle width
    // directly (no -2mm taper) — the mapped value IS already the buckle
    // end width.
    let buckleWidthMap = null;
    try {
      const rawMap = configurator.dataset.optionValueBuckleWidthMap;
      if (rawMap) buckleWidthMap = JSON.parse(rawMap);
    } catch (e) {
      console.warn('[VGreen] Failed to parse data-option-value-buckle-width-map JSON:', e);
    }
    let lastLugFromMap = false;

    function lugFromVariantOptions(options) {
      if (!Array.isArray(options)) return null;
      if (lugOptionPosition < 0 || lugOptionPosition >= options.length) return null;
      const value = String(options[lugOptionPosition] || '');
      if (buckleWidthMap && Object.prototype.hasOwnProperty.call(buckleWidthMap, value)) {
        lastLugFromMap = true;
        return parseInt(buckleWidthMap[value], 10);
      }
      lastLugFromMap = false;
      const m = value.match(/(\d+)/);
      return m ? parseInt(m[1], 10) : null;
    }

    function readLugFromSelectedVariantNode() {
      const node = document.querySelector(
        'variant-selects [data-selected-variant]'
      );
      if (!node) return null;
      try {
        const variant = JSON.parse(node.textContent);
        return lugFromVariantOptions(variant && variant.options);
      } catch (e) {
        return null;
      }
    }

    function legacyDomWalkLug() {
      const roots = [
        document.querySelector('variant-selects'),
        document.querySelector('variant-radios'),
        document,
      ].filter(Boolean);

      for (const root of roots) {
        const selects = root.querySelectorAll('select');
        for (const sel of selects) {
          if (/lug/i.test(sel.name || '')) {
            const m = (sel.value || '').match(/(\d+)/);
            if (m) return parseInt(m[1], 10);
          }
        }
        const radios = root.querySelectorAll('input[type="radio"]:checked');
        for (const r of radios) {
          if (/lug/i.test(r.name || '')) {
            const m = (r.value || '').match(/(\d+)/);
            if (m) return parseInt(m[1], 10);
          }
          const fieldset = r.closest('fieldset');
          const legend = fieldset && fieldset.querySelector('legend, .form__label');
          if (legend && /lug/i.test(legend.textContent)) {
            const m = (r.value || '').match(/(\d+)/);
            if (m) return parseInt(m[1], 10);
          }
        }
      }
      return null;
    }

    function getSelectedLugWidth() {
      if (cachedLugWidth !== null) return cachedLugWidth;
      // Init/refresh from data-selected-variant JSON.
      const fromNode = readLugFromSelectedVariantNode();
      if (fromNode !== null) {
        cachedLugWidth = fromNode;
        return cachedLugWidth;
      }
      // Last resort.
      return legacyDomWalkLug();
    }

    // -------- Tapered formula --------
    // (lug - lug%2) - 2 → round down to nearest even, then minus 2.
    // 18 → 16. 21 → 18. 22 → 20. 19 → 16. 23 → 20.
    // Clamped to [8, 26] — the manufacturing range. Below 8mm or above 26mm
    // we don't make buckles, so we fall back to the nearest manufactured
    // size and let the inventory filter narrow further.
    const MIN_BUCKLE_W = 8;
    const MAX_BUCKLE_W = 26;
    function taperedDefault(lug) {
      if (!lug) return null;
      if (lastLugFromMap) {
        // Direct mode: the value→width map already returned the buckle-end
        // width (e.g. PRX 40mm → 18). No taper, just clamp.
        if (lug < MIN_BUCKLE_W) return MIN_BUCKLE_W;
        if (lug > MAX_BUCKLE_W) return MAX_BUCKLE_W;
        return lug;
      }
      const evenLug = lug - (lug % 2);
      let t = evenLug - 2;
      if (t < MIN_BUCKLE_W) t = MIN_BUCKLE_W;
      if (t > MAX_BUCKLE_W) t = MAX_BUCKLE_W;
      return t;
    }

    // -------- Effective buckle width --------
    // From the dropdown if explicit; otherwise tapered formula on current lug.
    function getEffectiveBuckleWidth() {
      if (widthChoiceSelect && widthChoiceSelect.value) {
        const m = widthChoiceSelect.value.match(/(\d+)/);
        if (m) return parseInt(m[1], 10);
      }
      // "I'll let you decide" → tapered
      const lug = getSelectedLugWidth();
      return taperedDefault(lug);
    }

    function effectiveWidthLabel() {
      const w = getEffectiveBuckleWidth();
      return w ? w + 'mm' : '';
    }

    function parseAvailableWidths(radio) {
      // Shopify's `| join: ','` on a list-of-integer metafield outputs values
      // WITH surrounding square brackets (e.g. "[10,12,14,16]"). Strip them.
      const csv = (radio.dataset.availableWidths || '').replace(/[\[\]\s]/g, '');
      return csv
        .split(',')
        .map((s) => parseInt(s, 10))
        .filter((n) => !isNaN(n));
    }

    // -------- Sync hidden submit input --------
    function syncWidthSubmit() {
      if (!widthSubmitInput) return;
      widthSubmitInput.value = effectiveWidthLabel();
    }

    // -------- Filter the width-choice dropdown by lug --------
    // Hide options whose width is wider than the current lug. Keep
    // non-numeric options ("I'll let you decide") visible.
    function syncWidthChoiceOptions() {
      if (!widthChoiceSelect) return;
      const lug = getSelectedLugWidth();
      let currentValueStillVisible = true;
      Array.from(widthChoiceSelect.options).forEach((opt) => {
        const m = (opt.value || '').match(/(\d+)/);
        if (!m) {
          // Non-numeric (e.g. "I'll let you decide") — always show
          opt.hidden = false;
          opt.disabled = false;
          return;
        }
        const w = parseInt(m[1], 10);
        const tooWide = lug && w > lug;
        opt.hidden = !!tooWide;
        opt.disabled = !!tooWide;
        if (tooWide && opt.selected) {
          currentValueStillVisible = false;
        }
      });
      // If the previously-selected option just got hidden, fall back to
      // the first visible option (typically the "decide" entry).
      if (!currentValueStillVisible) {
        const firstVisible = Array.from(widthChoiceSelect.options).find(
          (o) => !o.hidden
        );
        if (firstVisible) {
          widthChoiceSelect.value = firstVisible.value;
        }
      }
    }

    // -------- Hide entire buckle section until lug chosen --------
    // Adds/removes `.vgreen-no-lug-chosen` on the configurator root.
    // CSS uses it to hide `.vgreen-buckle-section`.
    function syncBuckleSectionVisibility() {
      const lug = getSelectedLugWidth();
      configurator.classList.toggle('vgreen-no-lug-chosen', !lug);
    }

    // -------- Inventory filter --------
    function syncInventory() {
      syncBuckleSectionVisibility();
      const effective = getEffectiveBuckleWidth();
      const radios = getInventoryRadios();
      let firstVisible = null;
      let visibleCount = 0;

      radios.forEach((radio) => {
        const widths = parseAvailableWidths(radio);
        const ok = effective != null && widths.indexOf(effective) !== -1;
        const label = configurator.querySelector('label[for="' + radio.id + '"]');

        if (ok) {
          radio.classList.remove('vgreen-radio-option--hidden');
          if (label) label.classList.remove('vgreen-radio-option--hidden');
          radio.tabIndex = 0;
          if (!firstVisible) firstVisible = radio;
          visibleCount += 1;
        } else {
          radio.classList.add('vgreen-radio-option--hidden');
          if (label) label.classList.add('vgreen-radio-option--hidden');
          radio.tabIndex = -1;
          if (radio.checked) radio.checked = false;
        }
      });

      // Auto-check (only when source = provide). Restore the customer's
      // explicit choice if it is selectable again — even when an automatic
      // fallback is currently checked (choose X at 18mm, switch to 14mm where
      // it is filtered out, switch back: X must return). A newer genuine
      // selection overwrites lastExplicitBuckle, so this cannot override a
      // more recent decision. Otherwise authored default, then first visible.
      if (provideRadio && provideRadio.checked && firstVisible) {
        const visible = radios.filter(
          (r) => !r.classList.contains('vgreen-radio-option--hidden')
        );
        const remembered = lastExplicitBuckle
          ? visible.find((r) => r.value === lastExplicitBuckle)
          : null;
        if (remembered) {
          remembered.checked = true;
        } else if (!visible.some((r) => r.checked)) {
          const preferred = preferredDefaultRadio(visible);
          if (preferred) preferred.checked = true;
        }
      }

      // Empty-state note
      if (emptyNote) {
        emptyNote.hidden = visibleCount > 0;
      }

      syncWidthSubmit();
    }

    // -------- Mode (decide vs explicit) --------
    // When the customer leaves the width on "I'll let you decide", we hide
    // the VGreen-provides / Use-my-own toggle and show the inventory directly.
    // The reasoning: "decide" means the customer trusts us — no need to ask
    // them whether they're using their own buckle. They want OUR buckle.
    // When they pick a specific width they may be using their own buckle, so
    // we show the toggle.
    function syncBuckleMode() {
      const value = widthChoiceSelect ? widthChoiceSelect.value : '';
      const hasNumeric = !!(value && value.match(/\d+/));
      const isDecide = !hasNumeric;
      configurator.classList.toggle('vgreen-buckle-mode-decide', isDecide);
      if (isDecide) {
        // Force the provide path so inventory is visible.
        if (provideRadio) provideRadio.checked = true;
        if (ownRadio) ownRadio.checked = false;
      }
    }

    // -------- Source toggle --------
    function syncBuckleSource() {
      const isOwn = !!(ownRadio && ownRadio.checked);
      const radios = getInventoryRadios();

      // Disable inventory radios when own is selected so they don't submit
      radios.forEach((r) => {
        r.disabled = isOwn;
      });
      if (ownBuckleRadio) {
        ownBuckleRadio.disabled = !isOwn;
        if (isOwn) ownBuckleRadio.checked = true;
      }

      // File input: enabled only on the own branch. Clear any previously
      // chosen file AND any error message when toggling back to provide,
      // so we don't smuggle a stale photo onto a "VGreen provides" order.
      // `required` mirrors `disabled` — required-when-enabled. The form has
      // novalidate set in buy-buttons.liquid so HTML5 required doesn't
      // self-enforce; the capture-phase validator initRequireBucklePhoto
      // below is what actually blocks submit. Keeping `required` accurate
      // anyway for accessibility (screen readers announce it) and as
      // defense-in-depth if novalidate is ever removed.
      if (ownPhotoInput) {
        ownPhotoInput.disabled = !isOwn;
        ownPhotoInput.required = isOwn;
        if (!isOwn) {
          if (ownPhotoInput.value) ownPhotoInput.value = '';
          if (ownPhotoError) {
            ownPhotoError.hidden = true;
            ownPhotoError.textContent = '';
          }
        }
      }

      // Width input still submits in both branches (the customer can still
      // pick a width for the own-buckle path; if they leave it on "decide"
      // the tapered fallback is sent and we confirm by email).
      if (widthSubmitInput) widthSubmitInput.disabled = false;

      // When switching back to provide, ensure a visible inventory radio
      // is checked.
      if (!isOwn) {
        const visible = radios.filter(
          (r) => !r.classList.contains('vgreen-radio-option--hidden')
        );
        const any = visible.some((r) => r.checked);
        if (!any) {
          // Was `visible[0]`, which re-introduced entry #1 on the own ->
          // provide toggle (the own-branch radio shares properties[バックル],
          // so switching to it unchecks the inventory radio). Restore the
          // remembered / authored choice instead.
          const preferred = preferredDefaultRadio(visible);
          if (preferred) preferred.checked = true;
        }
      }

      syncWidthSubmit();
    }

    // -------- Inline file validation --------
    // Native browser validation on <input type=file> doesn't fire until form
    // submit, so we validate on change and show inline feedback right under
    // the field. Reject:
    //   - non-image types (defensive — accept attr already filters most)
    //   - files > data-max-mb (default 20MB — Shopify's per-LIP file cap)
    // On error, clear the input so the bad file isn't submitted.
    if (ownPhotoInput) {
      ownPhotoInput.addEventListener('change', () => {
        const file = ownPhotoInput.files && ownPhotoInput.files[0];
        if (!file) {
          if (ownPhotoError) {
            ownPhotoError.hidden = true;
            ownPhotoError.textContent = '';
          }
          return;
        }
        const maxMb = parseFloat(ownPhotoInput.dataset.maxMb || '20');
        const maxBytes = maxMb * 1024 * 1024;
        let err = '';
        if (file.type && file.type.indexOf('image/') !== 0) {
          err = '画像ファイルではないようです。JPG・PNG・HEIC・WebP形式の画像をアップロードしてください。';
        } else if (file.size > maxBytes) {
          err = 'ファイルサイズが ' + (file.size / 1024 / 1024).toFixed(1) +
  ' MB です。上限は ' + maxMb + ' MB です。小さいサイズのファイルをお試しください。';
        }
        if (err) {
          ownPhotoInput.value = '';
          if (ownPhotoError) {
            ownPhotoError.textContent = err;
            ownPhotoError.hidden = false;
          }
        } else if (ownPhotoError) {
          ownPhotoError.hidden = true;
          ownPhotoError.textContent = '';
        }
      });
    }

    // -------- Wire up listeners --------
    if (widthChoiceSelect) {
      widthChoiceSelect.addEventListener('change', () => {
        syncBuckleMode();
        syncBuckleSource();
        syncInventory();
      });
    }

    if (provideRadio) provideRadio.addEventListener('change', syncBuckleSource);
    if (ownRadio) ownRadio.addEventListener('change', syncBuckleSource);

    // -------- Single source of variant-change updates: Dawn's pubsub --------
    // Dawn publishes PUB_SUB_EVENTS.variantChange with `event.data.variant`
    // whenever the customer picks a different variant. We update our cached
    // lug width and re-run the syncs that depend on it. No DOM event
    // listeners on variant-selects, no document-level lug-name regex catch,
    // no setTimeout retry — the pubsub fires after Dawn's section refetch
    // completes, which is exactly when our cache should refresh.
    function applyVariantChange(variant) {
      const lug = lugFromVariantOptions(variant && variant.options);
      cachedLugWidth = lug; // can be null if customer is on placeholder
      syncWidthChoiceOptions();
      syncInventory();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (!event || !event.data) return;
        applyVariantChange(event.data.variant);
      });
    } else {
      // Fallback if Dawn's pubsub isn't loaded for some reason. Should not
      // happen on Dawn but keeps the configurator functional under unusual
      // theme setups.
      const variantSelects = document.querySelector('variant-selects');
      if (variantSelects) {
        variantSelects.addEventListener('change', () => {
          // Re-read from data-selected-variant after Dawn's section refetch.
          setTimeout(() => {
            cachedLugWidth = readLugFromSelectedVariantNode();
            syncWidthChoiceOptions();
            syncInventory();
          }, 50);
        });
      }
    }

    // Initial state — primes cachedLugWidth from data-selected-variant.
    cachedLugWidth = readLugFromSelectedVariantNode();
    syncWidthChoiceOptions();
    syncBuckleMode();
    syncInventory();
    syncBuckleSource();
  }

  // ============================================================
  // 4. Cart companions — surcharges as separate line items
  // ============================================================
  // Reads <script type="application/json" data-vgreen-companions> emitted
  // by the snippet. On Add-to-Cart, intercepts the form submit, posts the
  // main variant + matching companion variants to /cart/add.js in a single
  // items array, then redirects to /cart.
  //
  // Rule shape:
  //   matchByLip: { "<lip name>": [{ valueContainsAny: [...], variantId, title }] }
  //   matchByVariantOption: [{ optionNameMatches: <regex>, valueContainsAny: [...], variantId, title }]
  //
  // Match logic is case-insensitive substring on the LIP value or on the
  // selected variant option value.
  // ============================================================
  function initCartCompanions(configurator) {
    const dataNode = configurator.querySelector(
      'script[type="application/json"][data-vgreen-companions]'
    );
    if (!dataNode) return;

    let rules;
    try {
      rules = JSON.parse(dataNode.textContent);
    } catch (e) {
      console.error('[VGreen] Failed to parse companion rules JSON:', e);
      return;
    }

    // Find the product form via any input with form="..." attribute.
    const formAttrInput = configurator.querySelector('[form]');
    const formId = formAttrInput && formAttrInput.getAttribute('form');
    if (!formId) {
      console.warn('[VGreen] No form id found for companion hook.');
      return;
    }
    const form = document.getElementById(formId);
    if (!form) {
      console.warn('[VGreen] Form not found:', formId);
      return;
    }

    function matchesAny(value, needles) {
      const v = String(value || '').toLowerCase();
      return needles.some((n) => v.includes(String(n).toLowerCase()));
    }

    function readVariantOptions() {
      // Return array of {name, value} from variant-selects/variant-radios
      const root =
        document.querySelector('variant-selects') ||
        document.querySelector('variant-radios') ||
        document;
      const out = [];

      // Selects: name attribute is the option name in Dawn
      root.querySelectorAll('select').forEach((sel) => {
        out.push({ name: sel.name || sel.dataset.optionName || '', value: sel.value || '' });
      });

      // Radios: grouped by name; only checked one matters
      const seenNames = new Set();
      root.querySelectorAll('input[type="radio"]:checked').forEach((r) => {
        const n = r.name || '';
        if (seenNames.has(n)) return;
        seenNames.add(n);
        out.push({ name: n, value: r.value || '' });
      });
      return out;
    }

    function getMatchingCompanions() {
      const companions = [];
      const fd = new FormData(form);

      // matchByLip: read properties[<name>] from FormData
      const lipRules = rules.matchByLip || {};
      Object.keys(lipRules).forEach((lipName) => {
        const value = fd.get('properties[' + lipName + ']');
        if (!value) return;
        // First match wins per field: rules are ordered most-specific first
        // (e.g. "cartier" before "deployant") so a value containing both
        // tokens charges once, not twice.
        lipRules[lipName].some((rule) => {
          if (matchesAny(value, rule.valueContainsAny || [])) {
            companions.push({
              variantId: rule.variantId,
              title: rule.title,
              priceLabel: rule.priceLabel,
            });
            return true;
          }
          return false;
        });
      });

      // matchByVariantOption: read selected variant option values
      const variantOptions = readVariantOptions();
      (rules.matchByVariantOption || []).forEach((rule) => {
        const re = new RegExp(rule.optionNameMatches, 'i');
        variantOptions.forEach((opt) => {
          if (!re.test(opt.name)) return;
          if (matchesAny(opt.value, rule.valueContainsAny || [])) {
            companions.push({
              variantId: rule.variantId,
              title: rule.title,
              priceLabel: rule.priceLabel,
            });
          }
        });
      });

      // De-duplicate by variantId in case a rule double-fires
      const seen = new Set();
      return companions.filter((c) => {
        if (seen.has(c.variantId)) return false;
        seen.add(c.variantId);
        return true;
      });
    }

    // -------- Live summary banner --------
    // Renders "Your cart will include the strap plus these add-ons as
    // separate items: X (price), Y (price)." into the
    // [data-vgreen-companions-summary] element whenever LIPs / variant
    // change. Hidden when no companions match.
    //
    // Each companion's `priceLabel` is a pre-formatted string from
    // Liquid's `money` filter (e.g. "$10.00" / "CA$15.00" / "£8.00"),
    // already in the customer's active Markets currency. JS doesn't sum
    // or format — we'd need exchange rates we don't have client-side.
    // The cart breakdown shown by Shopify at /cart and checkout is the
    // source of truth for the total.
    const summaryEl = configurator.querySelector(
      '[data-vgreen-companions-summary]'
    );

    function renderSummary() {
      if (!summaryEl) return;
      const companions = getMatchingCompanions();
      if (companions.length === 0) {
        summaryEl.hidden = true;
        summaryEl.textContent = '';
        return;
      }
      const parts = companions.map((c) =>
        c.priceLabel ? c.title + ' (' + c.priceLabel + ')' : c.title
      );
      summaryEl.textContent = '';
      const lead = document.createElement('span');
      lead.textContent =
        'ストラップとは別に、以下の追加オプションが個別の商品としてカートに追加されます：';
      summaryEl.appendChild(lead);
      const items = document.createElement('strong');
      items.textContent = parts.join(', ');
      summaryEl.appendChild(items);
      const dot = document.createElement('span');
      dot.textContent = '。';
      summaryEl.appendChild(dot);
      summaryEl.hidden = false;
    }

    // Recompute on any LIP form change (radios + selects in our configurator)
    // OR on Dawn's variant change pubsub. Also catch UI-only radios with
    // names like `vgreen_ui_customize_*` — they don't submit but they
    // disable Tier 2 inputs, which changes which companions are active.
    configurator.addEventListener('change', (event) => {
      const t = event.target;
      if (!t) return;
      const name = t.name || '';
      if (
        name.indexOf('properties[') === 0 ||
        name.indexOf('vgreen_ui_') === 0 ||
        t.tagName === 'SELECT'
      ) {
        renderSummary();
      }
    });

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.variantChange
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, () => renderSummary());
    }

    // Initial paint after Dawn finishes mounting.
    renderSummary();
    setTimeout(renderSummary, 300);

    // Detect whether the form has any non-empty file LIPs to upload. /cart/add.js
    // accepts EITHER multipart/form-data (one item at a time, supports files)
    // OR application/json with items[] (many items, but binary uploads are
    // impossible because JSON has no native file representation). The hybrid
    // path below uses multipart for the main strap so the file travels, then
    // JSON for companions in a second request.
    function formHasFileUpload(fd) {
      for (const [, v] of fd.entries()) {
        if (v instanceof File && v.size > 0) return true;
      }
      return false;
    }

    async function handleSubmit(event) {
      const companions = getMatchingCompanions();
      const fdProbe = new FormData(form);
      const hasFile = formHasFileUpload(fdProbe);

      if (companions.length === 0 && !hasFile) {
        return; // No companions, no file — let Dawn handle the submit natively.
      }
      // If there's a file but no companions, Dawn's native multipart submit
      // would work fine — but Dawn's product-form.js intercepts submit and
      // posts FormData to /cart/add.js itself. That path already handles
      // files correctly when the form's enctype is multipart/form-data, so
      // we still let Dawn proceed in that case.
      if (companions.length === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      // Bundle every line in this add-to-cart with a shared group ID so
      // assets/vgreen-cart-grouping.js can cascade-remove them if the
      // customer drops one of them in the cart. The _ prefix hides these
      // from the customer-facing cart line item details.
      const bundleGroupId = generateBundleGroupId();
      const bundleGroupSize = String(1 + companions.length);

      const fd = new FormData(form);
      const variantId = parseInt(fd.get('id'), 10);
      const quantity = parseInt(fd.get('quantity') || '1', 10);

      // Lock the submit button
      const btn = form.querySelector('[type="submit"]');
      const btnOriginal = btn ? btn.innerHTML : null;
      if (btn) {
        btn.classList.add('loading');
        btn.disabled = true;
      }

      try {
        let res;

        if (hasFile) {
          // -------- Hybrid path: multipart for main + JSON for companions --------
          // Step 1: POST the main strap as multipart so the file uploads.
          // /cart/add.js accepts the same fields the standard product form
          // sends — we just need to strip non-product fields the form might
          // carry. The form already has id, quantity, properties[*], and
          // our file input.
          //
          // Note: /cart/add.js in multipart mode adds exactly one item. We
          // cannot include companions in this request; they go in Step 2.
          const mainOnlyFd = new FormData();
          for (const [k, v] of fd.entries()) {
            // Skip Dawn's section-render hint if present; otherwise pass
            // everything through (id, quantity, properties[*], file LIPs).
            if (k === 'sections' || k === 'sections_url') continue;
            mainOnlyFd.append(k, v);
          }
          // Bundle grouping properties (hidden from customer via `_` prefix).
          mainOnlyFd.append('properties[_vgreen_group_id]', bundleGroupId);
          mainOnlyFd.append('properties[_vgreen_group_size]', bundleGroupSize);
          mainOnlyFd.append('properties[_vgreen_group_role]', 'parent');

          res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/javascript' },
            // No Content-Type — let the browser set the multipart boundary.
            body: mainOnlyFd,
          });
          if (!res.ok) {
            const errBody = await res.text();
            throw new Error('Cart add (main, multipart) failed (' + res.status + '): ' + errBody);
          }

          // Step 2: POST companions as JSON (binary-safe; multiple items in
          // a single request).
          const companionItems = companions.map((c) => ({
            id: c.variantId,
            quantity,
            properties: {
              _vgreen_group_id: bundleGroupId,
              _vgreen_group_size: bundleGroupSize,
              _vgreen_group_role: 'companion',
            },
          }));
          res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
            },
            body: JSON.stringify({ items: companionItems }),
          });
          if (!res.ok) {
            const errBody = await res.text();
            throw new Error('Cart add (companions, JSON) failed (' + res.status + '): ' + errBody);
          }
        } else {
          // -------- Standard path: single JSON post with main + companions --------
          // Collect line item properties for the main item
          const properties = {};
          for (const [k, v] of fd.entries()) {
            const m = k.match(/^properties\[(.+)\]$/);
            if (m && typeof v === 'string' && v !== '') properties[m[1]] = v;
          }
          // Bundle grouping properties (hidden from customer via `_` prefix).
          properties._vgreen_group_id = bundleGroupId;
          properties._vgreen_group_size = bundleGroupSize;
          properties._vgreen_group_role = 'parent';

          const items = [
            Object.keys(properties).length > 0
              ? { id: variantId, quantity, properties }
              : { id: variantId, quantity },
          ];
          companions.forEach((c) => {
            items.push({
              id: c.variantId,
              quantity,
              properties: {
                _vgreen_group_id: bundleGroupId,
                _vgreen_group_size: bundleGroupSize,
                _vgreen_group_role: 'companion',
              },
            });
          });

          res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/javascript',
            },
            body: JSON.stringify({ items }),
          });

          if (!res.ok) {
            const errBody = await res.text();
            throw new Error('Cart add failed (' + res.status + '): ' + errBody);
          }
        }

        // Success — go to cart so customer sees the breakdown.
        // Reset button state BEFORE navigating so the browser's bfcache
        // doesn't snapshot the page in a "loading + disabled" state.
        // Without this, hitting the browser back button from /cart
        // restores the product page with the Add to Cart button stuck
        // showing only the spinner (no text, no clicks).
        if (btn) {
          btn.classList.remove('loading');
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
        }
        const spinner = form.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
        window.location.href = '/cart';
      } catch (err) {
        console.error('[VGreen] Companion cart add failed:', err);
        if (btn) {
          btn.classList.remove('loading');
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
          if (btnOriginal !== null) btn.innerHTML = btnOriginal;
        }
        const errSpinner = form.querySelector('.loading__spinner');
        if (errSpinner) errSpinner.classList.add('hidden');
        alert("カートに追加できませんでした。ページを再読み込みして再度お試しいただくか、お問い合わせください。");
      }
    }

    // Capture phase so we run before Dawn's product-form handler.
    form.addEventListener('submit', handleSubmit, true);
  }

  // ============================================================
  // 5. Custom-length hint
  // ============================================================
  // When the customer picks "Specify strap length in the Note field" on
  // the Strap length LIP dropdown, show a banner above the Notes textarea
  // asking them to specify exact length. Implemented by toggling
  // `.vgreen-strap-custom-mode` on the configurator root; CSS does the
  // rest.
  //
  // Strap length switched from variant axis to LIP on 2026-05-12, so this
  // function listens directly to the LIP <select> instead of subscribing
  // to Dawn's pubsub variant-change. (The notched and simple_strap
  // configurators already worked this way; the change here aligns
  // custom_strap and apple_watch with them.)
  function initCustomLengthHint(configurator) {
    const sel = configurator.querySelector(
      'select[name="properties[ストラップの長さ]"], select[name="properties[手首サイズ]"]'
    );
    if (!sel) return;

    function applyCurrent() {
      const isCustom = /(custom|カスタム)/i.test(String(sel.value || ''));
      configurator.classList.toggle('vgreen-strap-custom-mode', isCustom);
    }

    applyCurrent();
    sel.addEventListener('change', applyCurrent);
  }

  // ============================================================
  // 5b. Length-adjustment notice for single-fold clasps
  // ============================================================
  // Cartier-style + Omega-style clasps fold once and take up strap
  // length, so production may shorten the strap to suit. Show a notice
  // whenever such a clasp is the selected Buckle. Deployant options and
  // "use my own buckle" don't trigger it.
  //
  // JP NOTE: the Buckle radio submits the metaobject `label` (Japanese)
  // on this store — CO submits the English `value` — so the key is
  // properties[バックル] and the match is bilingual. Any Cartier/Omega
  // buckle metaobject added later MUST carry カルティエ or オメガ in
  // its JA label for this notice to fire.
  function initClaspLengthNotice(configurator) {
    const notice = configurator.querySelector('[data-clasp-length-notice]');
    if (!notice) return;
    function apply() {
      const b = configurator.querySelector(
        'input[name="properties[バックル]"]:checked'
      );
      notice.hidden =
        !(b && /(cartier|omega|カルティエ|オメガ)/i.test(b.value || ''));
    }
    configurator.addEventListener('change', apply);
    apply();
  }

  // ============================================================
  // 6. Require explicit variant selection (validator only)
  // ============================================================
  // dave added real Shopify variants whose Lug Width and Strap Length
  // option values literally read "Please choose a lug width" and
  // "Please choose a strap end". They sit at position 0 so they're the
  // default selection.
  //
  // We don't need to inject anything into the DOM. We just validate at
  // submit: if the currently-selected variant has any option value that
  // looks like a "please choose" placeholder, block the submit, scroll
  // the picker into view, focus the offending select, and show an
  // inline message above the variant picker.
  //
  // PLACEHOLDER_RE matches phrasings like:
  //   "Please choose a lug width" / "Choose a strap end" /
  //   "Please select width" / "— Please select size —"
  // Adjust the regex if dave names a future placeholder differently.
  // ============================================================
  function initRequireVariantSelection(configurator) {
    const formAttrInput = configurator.querySelector('[form]');
    const formId = formAttrInput && formAttrInput.getAttribute('form');
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form) return;

    // Two branches by intent:
    //   1. `please\s*(choose|select)` — matches the current admin labels
    //      "Please choose a lug width", "Please select size", etc.
    //   2. `^\s*choose\s+(a\s+)?(lug|strap|size|width|length|end)` — defensive
    //      catch-all for future renames where dave drops "Please" and writes
    //      e.g. "Choose a lug width" directly. Branch (2) doesn't fire on the
    //      current data; it exists so a label rename doesn't silently let
    //      placeholder variants pass validation. Keep both unless you also
    //      audit dave's variant labels.
    const PLACEHOLDER_RE = /please\s*(choose|select)|^\s*choose\s+(a\s+)?(lug|strap|size|width|length|end)|お選びください|選択してください/i;

    function getVariantSelects() {
      const root = document.querySelector('variant-selects');
      return root ? Array.from(root.querySelectorAll('select')) : [];
    }

    function getOptionLabel(select) {
      const id = select.id;
      if (id) {
        const lbl = document.querySelector('label[for="' + id + '"]');
        if (lbl) return lbl.textContent.trim();
      }
      return select.name || 'option';
    }

    function getInvalidSelects() {
      return getVariantSelects().filter((s) => {
        const v = String(s.value || '');
        return !v || PLACEHOLDER_RE.test(v);
      });
    }

    function flashRing(select) {
      // Remove + re-add so the CSS animation restarts even if the class
      // was already present from a previous click.
      select.classList.remove('vgreen-needs-choice');
      // Force reflow so the browser starts a fresh animation.
      void select.offsetWidth;
      select.classList.add('vgreen-needs-choice');
    }

    function validateAndBlockSubmit(event) {
      const invalid = getInvalidSelects();
      if (invalid.length === 0) return; // OK, let Dawn / companion handler proceed

      event.preventDefault();
      event.stopImmediatePropagation();

      // Ring + scroll + focus the first offending select. No notification
      // text — the field highlight + scroll-back is the entire UI.
      invalid.forEach(flashRing);
      const root = document.querySelector('variant-selects');
      if (root && root.scrollIntoView) {
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setTimeout(() => {
        try {
          invalid[0].focus({ preventScroll: true });
        } catch (e) {
          /* old browsers ignore the options arg */
        }
      }, 200);
    }

    // Capture phase on the form so we run BEFORE the companion submit
    // handler and Dawn's product-form handler.
    form.addEventListener('submit', validateAndBlockSubmit, true);

    // Clear the ring on the next interaction with the picker.
    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        if (!target || target.tagName !== 'SELECT') return;
        if (!getVariantSelects().includes(target)) return;
        target.classList.remove('vgreen-needs-choice');
      },
      true
    );
  }

  // ============================================================
  // 7. Engraving text gating
  // ============================================================
  // The engraving text input is conditionally REVEALED via CSS
  // when Engraving=Yes is selected — but its `name` attribute is
  // always present, so when CSS hides it, an empty value still
  // submits as `properties[刻印する文字] = ""`. Shopify's admin
  // order view renders the property label even with no value,
  // which clutters order details, packing slips, and CSV exports.
  //
  // Gate the input's enabled state directly off the Engraving
  // radio. Disabled inputs are excluded from FormData:
  //   Engraving=No  → input disabled  (won't submit)
  //   Engraving=Yes → input enabled   (customer fills; required
  //                                   non-empty value enforced by
  //                                   initRequireEngravingText below)
  //
  // We deliberately do NOT clear the value. If the customer
  // toggles No → Yes → No → Yes, their previous text is preserved
  // in the DOM, just suppressed from submission while Engraving=No.
  // ============================================================
  function initEngravingTextGating(configurator) {
    const textInput = configurator.querySelector(
      'input[name="properties[刻印する文字]"]'
    );
    if (!textInput) return;

    const radios = configurator.querySelectorAll(
      'input[type="radio"][name="properties[刻印]"]'
    );
    if (radios.length === 0) return;

    function sync() {
      const checked = configurator.querySelector(
        'input[type="radio"][name="properties[刻印]"]:checked'
      );
      // Match the same "Yes" prefix logic used in initRequireVariantSelection
      // (value reads like "Yes (+$15.00)").
      const isYes =
        checked && /^\s*(yes|はい)/i.test(String(checked.value || ''));
      textInput.disabled = !isYes;
    }

    sync();
    radios.forEach((r) => r.addEventListener('change', sync));
  }

  // ============================================================
  // 6b. Case diameter — gated by bund strap-style selection
  // ============================================================
  // Bund strap styles need the watch case diameter to size the leather
  // pad correctly. Same gating pattern as initEngravingTextGating:
  // disable the number input when Bund isn't checked so it stays out
  // of FormData; enable it when Bund is checked so the customer can
  // fill it in and the LIP submits. Required-non-empty enforcement
  // lives in initRequireBundCaseDiameter below.
  //
  // Value is intentionally NOT cleared on toggle so a customer who
  // bounces between strap styles doesn't lose their typed number.
  // ============================================================
  function initBundCaseDiameterGating(configurator) {
    const numberInput = configurator.querySelector(
      'input[name="properties[時計ケースの直径]"]'
    );
    if (!numberInput) return;

    const strapStyleRadios = configurator.querySelectorAll(
      'input[type="radio"][name="properties[ストラップのスタイル]"]'
    );
    if (strapStyleRadios.length === 0) return;

    // The case-diameter input lives inside .vgreen-conditional-panel--customize,
    // which means initCustomizeToggle.syncTier2State() blanket-enables it
    // whenever customize flips to Yes — even if bund isn't selected. Without
    // re-syncing on customize toggle, an enabled-but-hidden input could let
    // an empty Case diameter LIP slip through Dawn's fallback submit
    // (initStripEmptyProperties scopes to input[type="text"] only, not number).
    // Listen to the customize radios too and run sync AFTER syncTier2State
    // has had its turn (registration order — initCustomizeToggle runs first).
    const customizeYes = configurator.querySelector(
      'input[data-vgreen-conditional="customize-yes"]'
    );
    const customizeRadios = customizeYes
      ? configurator.querySelectorAll(
          'input[name="' + customizeYes.name + '"]'
        )
      : [];

    function sync() {
      // Force-disable when the Tier 2 panel is collapsed — even if bund
      // is the default-checked first metaobject entry — so customize=No
      // never lets an empty Case diameter through.
      const isCustomizeOn = customizeYes && customizeYes.checked;
      const checked = configurator.querySelector(
        'input[type="radio"][name="properties[ストラップのスタイル]"]:checked'
      );
      const isBund =
        checked && /(bund|ブンド)/i.test(String(checked.value || ''));
      numberInput.disabled = !(isCustomizeOn && isBund);
    }

    sync();
    strapStyleRadios.forEach((r) => r.addEventListener('change', sync));
    customizeRadios.forEach((r) => r.addEventListener('change', sync));
  }

  // ============================================================
  // 7a. Strap length LIP — required (always)
  // ============================================================
  // Mirrors initRequireVariantSelection's capture-phase block-submit
  // pattern, but for the Strap length LIP <select> (which carries the
  // strap-length axis since it moved from a variant axis to LIP on
  // 2026-05-12). HTML5 `required` on the select would normally pop a
  // native validation tooltip, but that gets bypassed when our
  // companion-cart handler hijacks submit. Explicit validator here
  // guarantees the block fires consistently and matches the same
  // ring + scroll UX as the lug-width placeholder validator.
  //
  // Registered AFTER initRequireVariantSelection (lug width must be
  // fixed first; their tooltips don't stack), BEFORE initCartCompanions.
  // ============================================================
  function initRequireStrapLength(configurator) {
    const formAttrInput = configurator.querySelector('[form]');
    const formId = formAttrInput && formAttrInput.getAttribute('form');
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form) return;

    const sel = configurator.querySelector(
      'select[name="properties[ストラップの長さ]"], select[name="properties[手首サイズ]"]'
    );
    if (!sel) return; // Notched-only products with no strap-length LIP — skip

    function validateAndBlockSubmit(event) {
      const value = String(sel.value || '').trim();
      if (value !== '') return; // Real strap length picked — pass

      event.preventDefault();
      event.stopImmediatePropagation();

      // Ring-flash the empty select (same .vgreen-needs-choice class
      // the variant validator uses).
      sel.classList.remove('vgreen-needs-choice');
      void sel.offsetWidth;
      sel.classList.add('vgreen-needs-choice');

      if (sel.scrollIntoView) {
        sel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setTimeout(() => {
        try {
          sel.focus({ preventScroll: true });
        } catch (e) {
          /* old browsers ignore the options arg */
        }
      }, 200);
    }

    form.addEventListener('submit', validateAndBlockSubmit, true);

    // Clear the ring as soon as the customer picks a real size.
    sel.addEventListener('change', () => {
      sel.classList.remove('vgreen-needs-choice');
    });
  }

  // ============================================================
  // 7b. Engraving text — required when Engraving=Yes
  // ============================================================
  // Mirrors initRequireVariantSelection's capture-phase block-submit
  // pattern. The CSS conditional reveal exposes the text input when
  // the customer picks Engraving=Yes, but a customer can pick Yes and
  // submit with the text field still empty — the engraving companion
  // gets added to the cart and the strap maker has nothing to engrave.
  //
  // Validate at submit time: if Engraving=Yes AND the text is empty,
  // preventDefault + stopImmediatePropagation, ring-flash the field,
  // scroll-to + focus. Same UX as the placeholder-variant validator.
  //
  // Registered BEFORE initCartCompanions in initConfigurator so the
  // companion-cart fetch never fires when this check fails.
  // ============================================================
  function initRequireEngravingText(configurator) {
    const formAttrInput = configurator.querySelector('[form]');
    const formId = formAttrInput && formAttrInput.getAttribute('form');
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form) return;

    const textInput = configurator.querySelector(
      'input[name="properties[刻印する文字]"]'
    );
    if (!textInput) return;

    function validateAndBlockSubmit(event) {
      const checked = configurator.querySelector(
        'input[type="radio"][name="properties[刻印]"]:checked'
      );
      // Match the same "Yes" prefix logic used elsewhere
      // (value reads like "Yes (+$15.00)").
      const isYes =
        checked && /^\s*(yes|はい)/i.test(String(checked.value || ''));
      if (!isYes) return; // No engraving — nothing to validate
      const value = String(textInput.value || '').trim();
      if (value !== '') return; // Has text — pass

      event.preventDefault();
      event.stopImmediatePropagation();

      // Ring-flash the empty text input (same .vgreen-needs-choice
      // class the variant validator uses; the CSS animation works on
      // any element).
      textInput.classList.remove('vgreen-needs-choice');
      void textInput.offsetWidth;
      textInput.classList.add('vgreen-needs-choice');

      if (textInput.scrollIntoView) {
        textInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setTimeout(() => {
        try {
          textInput.focus({ preventScroll: true });
        } catch (e) {
          /* old browsers ignore the options arg */
        }
      }, 200);
    }

    form.addEventListener('submit', validateAndBlockSubmit, true);

    // Clear the ring as soon as the customer starts typing.
    textInput.addEventListener('input', () => {
      textInput.classList.remove('vgreen-needs-choice');
    });
  }

  // ============================================================
  // 7c. Case diameter — required when Bund is selected
  // ============================================================
  // Mirrors initRequireEngravingText. The gating function above keeps
  // the input out of FormData when Bund isn't selected, but a customer
  // CAN pick Bund and submit with the field blank — that ships a bund
  // strap to production with no case-diameter spec, which means an
  // email chase or a wrongly-sized leather pad. Validate at submit:
  // if Bund is checked and the input is empty, block submit with the
  // same ring-flash + scroll UX as the other validators.
  //
  // Defensive check on visibility — if a customer somehow ends up
  // with Bund checked while the Customize panel is collapsed (e.g.
  // bund happens to be the metaobject's first entry), don't block
  // them on an invisible field. offsetParent === null means the
  // input or one of its ancestors has display:none.
  //
  // Registered alongside the other capture-phase submit listeners in
  // initConfigurator so it runs before initCartCompanions.
  // ============================================================
  function initRequireBundCaseDiameter(configurator) {
    const formAttrInput = configurator.querySelector('[form]');
    const formId = formAttrInput && formAttrInput.getAttribute('form');
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form) return;

    const numberInput = configurator.querySelector(
      'input[name="properties[時計ケースの直径]"]'
    );
    if (!numberInput) return;

    function validateAndBlockSubmit(event) {
      const checked = configurator.querySelector(
        'input[type="radio"][name="properties[ストラップのスタイル]"]:checked'
      );
      const isBund =
        checked && /(bund|ブンド)/i.test(String(checked.value || ''));
      if (!isBund) return; // Not a bund strap — nothing to validate
      if (numberInput.offsetParent === null) return; // Field hidden — skip
      const value = String(numberInput.value || '').trim();
      if (value !== '') return; // Has a number — pass

      event.preventDefault();
      event.stopImmediatePropagation();

      numberInput.classList.remove('vgreen-needs-choice');
      void numberInput.offsetWidth;
      numberInput.classList.add('vgreen-needs-choice');

      if (numberInput.scrollIntoView) {
        numberInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setTimeout(() => {
        try {
          numberInput.focus({ preventScroll: true });
        } catch (e) {
          /* old browsers ignore the options arg */
        }
      }, 200);
    }

    form.addEventListener('submit', validateAndBlockSubmit, true);

    // Clear the ring as soon as the customer starts typing.
    numberInput.addEventListener('input', () => {
      numberInput.classList.remove('vgreen-needs-choice');
    });
  }

  // ============================================================
  // 7d. Buckle photo — required when "I'll use my own buckle"
  // ============================================================
  // Mirrors initRequireEngravingText / initRequireBundCaseDiameter.
  // The buckle photo file input lives inside the buckle-own conditional
  // panel and is enabled by syncBuckleSource only when the customer
  // picks "I'll use my own buckle". When that branch is active, a photo
  // is required — production needs it to size/match the strap end. If
  // the input is enabled but empty at submit time, block the form,
  // ring-flash the file field, scroll-to + focus.
  //
  // Skips when ownRadio doesn't exist (configurator has no buckle
  // section) or when ownPhotoInput doesn't exist (configurator has a
  // buckle source toggle but no inline upload — defensive; not the
  // current case for any active configurator type).
  //
  // Registered alongside the other capture-phase submit listeners in
  // initConfigurator so it runs before initCartCompanions.
  // ============================================================
  function initRequireBucklePhoto(configurator) {
    const formAttrInput = configurator.querySelector('[form]');
    const formId = formAttrInput && formAttrInput.getAttribute('form');
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form) return;

    const ownRadio = configurator.querySelector(
      'input[data-vgreen-conditional="buckle-own"]'
    );
    const fileInput = configurator.querySelector(
      '.vgreen-buckle-photo-input'
    );
    if (!ownRadio || !fileInput) return;

    const errorEl = configurator.querySelector('.vgreen-file-error');

    function validateAndBlockSubmit(event) {
      if (!ownRadio.checked) return; // VGreen-provides branch — nothing to validate
      if (fileInput.files && fileInput.files.length > 0) return; // Photo present — pass

      event.preventDefault();
      event.stopImmediatePropagation();

      // Surface our own error message under the field — clearer than
      // a generic ring-flash for a file input that customers may not
      // visually associate with the "needs choice" treatment.
      if (errorEl) {
        errorEl.textContent =
          'ストラップのエンド形状を合わせるため、バックルの写真をアップロードしてください。';
        errorEl.hidden = false;
      }

      fileInput.classList.remove('vgreen-needs-choice');
      void fileInput.offsetWidth;
      fileInput.classList.add('vgreen-needs-choice');

      if (fileInput.scrollIntoView) {
        fileInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setTimeout(() => {
        try {
          fileInput.focus({ preventScroll: true });
        } catch (e) {
          /* old browsers ignore the options arg */
        }
      }, 200);
    }

    form.addEventListener('submit', validateAndBlockSubmit, true);

    // Clear the ring + our error message as soon as a valid file is
    // picked. (The existing change handler upstream still runs first
    // for type/size validation; if it rejects the file it sets its own
    // error and clears fileInput.value, in which case files.length is
    // 0 and we leave the ring on.)
    fileInput.addEventListener('change', () => {
      if (fileInput.files && fileInput.files.length > 0) {
        fileInput.classList.remove('vgreen-needs-choice');
        if (errorEl && errorEl.textContent && errorEl.textContent.indexOf('バックルの写真') !== -1) {
          errorEl.hidden = true;
          errorEl.textContent = '';
        }
      }
    });
  }

  // ============================================================
  // 8. Strip empty optional properties on submit
  // ============================================================
  // The companion-cart path (initCartCompanions handleSubmit)
  // already filters empty `properties[*]` values before posting
  // to /cart/add.js (see the `v !== ''` check when building the
  // properties object). But when no companion rule matches,
  // control falls through to Dawn's default form submit, which
  // sends the raw FormData including empty `properties[*]`.
  // That's how empty `Engraving text` and `Notes` ended up on
  // order details and packing slips for plain (no-customize)
  // orders before this fix.
  //
  // Fix: at submit time, briefly disable any empty text/textarea
  // `properties[*]` inputs so neither code path picks them up,
  // then re-enable on next tick so bfcache restores don't snapshot
  // them disabled.
  //
  // Skips inputs already disabled (e.g. Tier 2 inputs disabled by
  // the Customize toggle, or Engraving text disabled by the gating
  // above) — those will be cleaned up on their own and we don't
  // want to re-enable them in the setTimeout fallback.
  //
  // Registered AFTER initRequireVariantSelection in initConfigurator
  // so validator-blocked submits never reach this step (the
  // validator calls stopImmediatePropagation, preventing later
  // capture-phase listeners on the same form from running).
  // ============================================================
  function initStripEmptyProperties(configurator) {
    const formAttrInput = configurator.querySelector('[form]');
    const formId = formAttrInput && formAttrInput.getAttribute('form');
    if (!formId) return;
    const form = document.getElementById(formId);
    if (!form) return;

    form.addEventListener(
      'submit',
      function () {
        const toReenable = [];
        // BUG FIX 2026-05-11: the configurator's text inputs and Notes
        // textarea are NOT descendants of the product form — they live
        // inside the .vgreen-configurator div and associate with the
        // form via `form="..."` attribute. `form.querySelectorAll`
        // therefore found ZERO matches all along, and empty Watch model
        // + Notes properties have been slipping into every order's line
        // item properties (e.g. "Watch model" with no value). Scope the
        // query to the configurator div instead.
        configurator
          .querySelectorAll(
            'input[type="text"][name^="properties["], textarea[name^="properties["]'
          )
          .forEach((input) => {
            if (input.disabled) return;
            if ((input.value || '').trim() !== '') return;
            input.disabled = true;
            toReenable.push(input);
          });
        // Re-enable on next tick. By then both the companion
        // handler's FormData snapshot and Dawn's product-form
        // FormData snapshot have been built (both are synchronous
        // in their respective submit listeners).
        if (toReenable.length > 0) {
          setTimeout(() => {
            toReenable.forEach((i) => {
              i.disabled = false;
            });
          }, 0);
        }
      },
      true
    );
  }

  // ============================================================
  // 9. Pre-select lug width from URL query param
  // ============================================================
  // When the customer arrives via the Lug Width Finder or Artisan
  // Stylist with `?lug_width=20` in the URL, find the Lug Width
  // dropdown in Dawn's variant-selects and set it to the matching
  // value. Triggers a `change` event so Dawn refetches the section
  // and updates the active variant — which in turn fires the
  // PUB_SUB_EVENTS.variantChange that initBuckle is already
  // listening for, so the buckle inventory rebuilds automatically.
  //
  // Runs ONCE per page load. If the URL has no `lug_width`, this
  // is a no-op. If the URL has a value that doesn't match any
  // option (e.g. 33mm when the product only carries 8–32), we
  // silently leave the picker on its default — the customer will
  // see no match and pick manually.
  // ============================================================
  function initPreselectFromUrl() {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }
    const requestedLug = params.get('lug_width');
    if (!requestedLug) return;

    // Normalize "20", "20mm", "20 mm" → integer 20
    const m = String(requestedLug).match(/(\d+)/);
    if (!m) return;
    const target = parseInt(m[1], 10);
    if (!target) return;

    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return;

    // Find the <select> whose label contains "lug" (case-insensitive).
    // Dawn renders variant option selects with the option name as
    // their `name` attribute (e.g. name="Lug Width").
    const selects = Array.from(variantSelects.querySelectorAll('select'));
    const lugSelect = selects.find((sel) => {
      if (/lug/i.test(sel.name || '')) return true;
      const label =
        sel.id && document.querySelector('label[for="' + sel.id + '"]');
      return !!(label && /lug/i.test(label.textContent || ''));
    });
    if (!lugSelect) return;

    // Find the option whose value contains the target integer.
    const matchOption = Array.from(lugSelect.options).find((opt) => {
      const om = String(opt.value || '').match(/(\d+)/);
      return om && parseInt(om[1], 10) === target;
    });
    if (!matchOption) return; // No match — leave the picker alone.

    // Already on the right value? Skip the change event so we don't
    // trigger an unnecessary section refetch.
    if (lugSelect.value === matchOption.value) return;

    lugSelect.value = matchOption.value;
    lugSelect.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // ============================================================
  // Bootstrapping
  // ============================================================
  function initConfigurator(configurator) {
    initPreserveUrlParams();
    initCustomizeToggle(configurator);
    initStitchColorGating(configurator); // after initCustomizeToggle — see note there
    initBuckle(configurator);
    initEngravingTextGating(configurator);
    initBundCaseDiameterGating(configurator);
    // Capture-phase submit listener registration order matters —
    // listeners fire in the order they were attached. The chain is:
    //   1. initRequireVariantSelection  — validate variant selects;
    //                                     stopImmediatePropagation when
    //                                     invalid so later listeners
    //                                     (and Dawn's handler) never run.
    //   2. initRequireEngravingText     — validate Engraving=Yes has
    //                                     non-empty text. Same block
    //                                     pattern as variant validator.
    //   3. initRequireBundCaseDiameter  — validate Style=Bund has a
    //                                     non-empty case-diameter mm value.
    //   4. initStripEmptyProperties     — disable empty optional text/
    //                                     textarea LIP inputs so neither
    //                                     the companion FormData nor
    //                                     Dawn's FormData picks them up.
    //   5. initCartCompanions           — read FormData, post companion
    //                                     items to /cart/add.js, redirect
    //                                     to /cart. Falls through to Dawn
    //                                     if no companion rule matches.
    initRequireVariantSelection(configurator);
    initRequireStrapLength(configurator);
    initRequireEngravingText(configurator);
    initRequireBundCaseDiameter(configurator);
    initRequireBucklePhoto(configurator);
    initStripEmptyProperties(configurator);
    initCartCompanions(configurator);
    initCustomLengthHint(configurator);
    initClaspLengthNotice(configurator);
  }

  function initAll(root) {
    (root || document)
      .querySelectorAll('.vgreen-configurator')
      .forEach(initConfigurator);
  }

  // Run once per page load (NOT per section load — we don't want
  // to keep re-applying the URL param if the customer has since
  // changed their selection manually).
  let preselectAlreadyRan = false;
  function maybeRunPreselect() {
    if (preselectAlreadyRan) return;
    preselectAlreadyRan = true;
    // Defer until after Dawn's variant-selects has primed itself
    // and our initConfigurator has set up the buckle pubsub.
    setTimeout(initPreselectFromUrl, 0);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initAll();
      maybeRunPreselect();
    });
  } else {
    initAll();
    maybeRunPreselect();
  }
  // ============================================================
  // bfcache restore safety net
  // ============================================================
  // When a customer clicks Add to Cart and the companion handler
  // navigates to /cart, then they hit browser back, modern browsers
  // restore the product page from the back-forward cache. The DOM
  // is restored in whatever state it was when navigation began —
  // including the submit button's `loading` class and `disabled`
  // attribute (which our handler had just applied). This makes the
  // button appear empty and unclickable on the restored page.
  //
  // The handler above now resets the button BEFORE navigating, but
  // this listener is the second line of defense: any time the page
  // is restored from bfcache (event.persisted === true), reset all
  // Cheap, runs only on bfcache restores, no effect on cold loads.
  window.addEventListener('pageshow', (event) => {
    // Reload the product page when the customer returns to it from /cart,
    // because the configurator may have stale `disabled` flags on inputs
    // (e.g. inventory radios disabled from an own-buckle add-to-cart that
    // just completed, or Tier 2 inputs disabled from a Customize=No state).
    // Trying to surgically reset each dependent input proved fragile;
    // reloading is the safest path.
    //
    // We have to detect "returned from cart" via two channels because the
    // file input we ship for buckle photo uploads makes the page bfcache-
    // ineligible in most browsers — so the `pageshow.persisted` flag is
    // rarely true for this configurator. Channels:
    //   A. bfcache restore (event.persisted === true) — fires when the
    //      page IS bfcache-eligible (no file input present, no unload
    //      handlers, etc.). Reload immediately.
    //   B. Fresh navigation but document.referrer points to /cart — this
    //      catches the file-input-blocked case. Guarded by sessionStorage
    //      so we don't loop (the reload itself preserves referrer).
    if (!document.querySelector('.vgreen-configurator')) return;

    if (event.persisted) {
      navigateToCleanProductUrl();
      return;
    }

    // Already reloaded once via one of the non-bfcache paths? Don't loop.
    if (sessionStorage.getItem('vg_cart_back_reload') === '1') {
      sessionStorage.removeItem('vg_cart_back_reload');
      return;
    }

    // Three independent signals can indicate "customer is returning from /cart":
    //   A. document.referrer matches /cart (most reliable when set).
    //   B. Performance API says navigationType === 'back_forward' (back/forward
    //      button click). Some browsers report this; some don't, depending on
    //      whether bfcache was even attempted.
    //   C. (Best-effort) — could add more, but A+B catch the realistic flows.
    // If ANY signal fires, reload. The sessionStorage guard prevents loops
    // regardless of which signal triggered.
    let shouldReload = false;

    try {
      const ref = document.referrer;
      if (ref) {
        const refPath = new URL(ref, location.origin).pathname;
        if (refPath === '/cart' || refPath.startsWith('/cart/')) {
          shouldReload = true;
        }
      }
    } catch (_) {
      // Malformed referrer URL — ignore.
    }

    if (!shouldReload) {
      try {
        const navEntry =
          performance.getEntriesByType &&
          performance.getEntriesByType('navigation')[0];
        if (navEntry && navEntry.type === 'back_forward') {
          shouldReload = true;
        } else if (
          performance.navigation &&
          performance.navigation.type === 2
        ) {
          // Legacy Performance Navigation API. type === 2 = TYPE_BACK_FORWARD.
          shouldReload = true;
        }
      } catch (_) {
        /* No-op */
      }
    }

    if (shouldReload) {
      sessionStorage.setItem('vg_cart_back_reload', '1');
      navigateToCleanProductUrl();
    }
  });

  // Strip Dawn's `?variant=ID` query param and reload. Dawn reads that param
  // on page load and uses it to pre-select a specific variant — which is
  // exactly what we DON'T want when the customer is returning from /cart.
  // Without stripping it, the variant (and thus the lug width) stays
  // preselected after reload, defeating the whole purpose of the reset.
  //
  // We preserve any other query params (e.g. `?lug_width=X` used by the
  // Lug Width Finder pre-select flow — initPreselectFromUrl picks it up).
  function navigateToCleanProductUrl() {
    try {
      const url = new URL(location.href);
      url.searchParams.delete('variant');
      const target = url.pathname + url.search + url.hash;
      // Use replace so the customer's back button can't undo our cleanup
      // (otherwise they'd land back on /cart again, triggering this loop).
      window.location.replace(target);
    } catch (e) {
      // Malformed URL — fall back to plain reload. Still better than nothing.
      window.location.reload();
    }
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
